/**
 * Tema dinâmico buscando do backend
 *
 * Referências:
 * 1. https://stackblitz.com/edit/angular-material-theming-playground?file=src%2Fapp%2Fapp.component.ts%3AL77
 * 2. https://webfactory.mk/an-approach-to-dynamic-theme-color-generation-in-angular/
 */

import { Injectable, OnDestroy, Renderer2, RendererFactory2 } from '@angular/core';
import { TinyColor, tinycolor } from '@ctrl/tinycolor';
import { ParametrosRepository } from '@data/repository/parametros/parametros.repository';

export interface Color {
  name: string;
  hex: string;
  darkContrast: boolean;
}

@Injectable({
  providedIn: 'root',
})
class InitializationService implements OnDestroy {
  private static readonly THEME_VARIABLE_PREFIX = '--theme';
  private readonly renderer: Renderer2;

  private primaryColorPalette: Color[] = [];
  private accentColorPalette: Color[] = [];

  constructor(
    private rendererFactory: RendererFactory2,
    private parametrosRepository: ParametrosRepository,

  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  ngOnDestroy(): void {
    this.renderer?.destroy();
  }

  async initialize(router?: string): Promise<any> {
    const promise = new Promise((resolve, reject) => {
      this.parametrosRepository.get(router).subscribe(
        ({ Data }: any) => {

          sessionStorage.setItem('habilitarRedirecionamentoIMOB', Data.HabilitarRedirecionamentoIMOB)
     
          const solicitarDocumentos = {
            SolicitarSSN: Data.SolicitarSSN || 'N',
            SolicitarITIN: Data.SolicitarITIN || 'N',
            SolicitarPassaport: Data.SolicitarPassaport || 'N',
            SolicitarDriveLicense: Data.SolicitarDriveLicense || 'N',
          }
       
          sessionStorage.setItem('solicitarDocumentos', JSON.stringify(solicitarDocumentos));

          if (Data.CorPrincipalReservaWeb && Data.CorSecundariaReservaWeb) {
            // Manipulação dos dados bem-sucedida
            this.primaryColorPalette = this.computeColors(Data.CorPrincipalReservaWeb);
            this.accentColorPalette = this.computeColors(Data.CorSecundariaReservaWeb);
          } else {
            this.primaryColorPalette = this.computeColors('#000000');
            this.accentColorPalette = this.computeColors('#000000');
          }
          this.updateTheme(this.primaryColorPalette, 'primary');
          this.updateTheme(this.accentColorPalette, 'accent');

          if (Data.CorPrincipalReservaWeb && Data.CorSecundariaReservaWeb) {
            sessionStorage.setItem('primaryColor', `#${Data.CorPrincipalReservaWeb}`);
            sessionStorage.setItem('accentColor', `#${Data.CorSecundariaReservaWeb}`);
          } else {
            sessionStorage.setItem('primaryColor', `#000000`);
            sessionStorage.setItem('accentColor', `#000000`);
          }
          sessionStorage.setItem('urlLogo', Data.UrlLogoReservaWeb);
          sessionStorage.setItem('urlContratoLocacao', Data.UrlContratoLocacao);
          sessionStorage.setItem('termosCondicoes', Data.TermosCondicoes);
          sessionStorage.setItem('modoUtilizacao', Data.ModoExibicaoFrame);
          sessionStorage.setItem('pagamentoStripe', Data.HabilitarFormularioPagamentoStripe);
          sessionStorage.setItem('authorization', Data.Authorization);

          if (Data.UrlLogoFavicon || Data.UrlLogoReservaWeb) {
            const favicon = document.querySelector("link[rel*='icon']") as HTMLLinkElement;
            favicon?.setAttribute('href', Data.UrlLogoFavicon || Data.UrlLogoReservaWeb);
          }

          resolve({
            primaryColor: Data.CorPrincipalReservaWeb,
            accentColor: Data.CorSecundariaReservaWeb,
            urlLogo: Data.UrlLogoReservaWeb,
            Router: Data.Router,
          });
        },
        (error) => {
          // Manipulação de erro
          console.error('Ocorreu um erro ao obter os dados do repositório:', error);
          reject(false); // Rejeitar a promessa com false em caso de erro
        }
      );
    });

    return promise;
  }

  private getColor(value: TinyColor, name: string): Color {
    const color = tinycolor(value);

    return {
      name,
      hex: color.toHexString(),
      darkContrast: color.isLight(),
    };
  }

  private computeColors(hex: string): Color[] {
    return [
      this.getColor(tinycolor(hex).lighten(52), '50'),
      this.getColor(tinycolor(hex).lighten(37), '100'),
      this.getColor(tinycolor(hex).lighten(26), '200'),
      this.getColor(tinycolor(hex).lighten(12), '300'),
      this.getColor(tinycolor(hex).lighten(6), '400'),
      this.getColor(tinycolor(hex), '500'),
      this.getColor(tinycolor(hex).darken(6), '600'),
      this.getColor(tinycolor(hex).darken(12), '700'),
      this.getColor(tinycolor(hex).darken(18), '800'),
      this.getColor(tinycolor(hex).darken(24), '900'),
      this.getColor(tinycolor(hex).lighten(50).saturate(30), 'A100'),
      this.getColor(tinycolor(hex).lighten(30).saturate(30), 'A200'),
      this.getColor(tinycolor(hex).lighten(10).saturate(15), 'A400'),
      this.getColor(tinycolor(hex).lighten(5).saturate(5), 'A700'),
    ];
  }

  private updateTheme(colors: Color[], theme: string) {
    colors.forEach((color) => {
      document.documentElement.style.setProperty(
        `${InitializationService.THEME_VARIABLE_PREFIX}-${theme}-${color.name}`,
        color.hex
      );

      document.documentElement.style.setProperty(
        `${InitializationService.THEME_VARIABLE_PREFIX}-${theme}-contrast-${color.name}`,
        color.darkContrast ? 'rgba(black, 0.87)' : 'white'
      );
    });
  }
}
export { InitializationService };
